import { useState, useEffect } from 'react';

const Footer = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <footer 
      className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white dark:from-dark-900 dark:to-dark-950"
      onMouseMove={handleMouseMove}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating orbs */}
        <div 
          className="absolute w-64 h-64 rounded-full opacity-20 blur-3xl transition-all duration-1000 ease-out"
          style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #eab308 100%)',
            left: `${mousePosition.x * 0.3}%`,
            top: `${mousePosition.y * 0.5 - 20}%`,
            transform: 'translate(-50%, -50%)',
          }}
        />
        <div 
          className="absolute w-48 h-48 rounded-full opacity-15 blur-3xl transition-all duration-700 ease-out"
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
            right: `${100 - mousePosition.x * 0.2}%`,
            bottom: `${mousePosition.y * 0.3}%`,
            transform: 'translate(50%, 50%)',
          }}
        />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(239, 68, 68, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(239, 68, 68, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-primary-500/30 animate-pulse"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + i * 0.5}s`,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div 
          className="group relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* 3D Card Container */}
          <div 
            className="relative flex justify-center items-center py-8 px-6 rounded-2xl 
                       bg-white/50 dark:bg-dark-800/50 backdrop-blur-xl
                       border border-gray-200/50 dark:border-dark-700/50
                       shadow-lg shadow-gray-200/50 dark:shadow-dark-900/50
                       transition-all duration-500 ease-out
                       hover:shadow-2xl hover:shadow-primary-500/20 dark:hover:shadow-primary-500/10
                       hover:border-primary-300/50 dark:hover:border-primary-700/50
                       hover:-translate-y-1"
            style={{
              transform: isHovered 
                ? `perspective(1000px) rotateX(${(mousePosition.y - 50) * 0.05}deg) rotateY(${(mousePosition.x - 50) * 0.05}deg) translateY(-4px)`
                : 'perspective(1000px) rotateX(0deg) rotateY(0deg)',
              transition: 'transform 0.3s ease-out, box-shadow 0.5s ease-out, border-color 0.5s ease-out',
            }}
          >
            {/* Shine effect */}
            <div 
              className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
              style={{
                background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.15) 0%, transparent 50%)`,
              }}
            />

            {/* Content */}
            <div className="relative flex items-center gap-3">
              {/* Animated heart */}
              <span className="text-lg select-none">Built with</span>
              <span 
                className="text-2xl animate-pulse inline-block transform transition-transform duration-300 group-hover:scale-125"
                style={{
                  filter: isHovered ? 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.6))' : 'none',
                }}
              >
                ❤️
              </span>
              <span className="text-lg select-none">by</span>
              
              {/* Interactive Link */}
              <a 
                href="https://techycsr.dev" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative group/link ml-1"
              >
                <span className="relative z-10 text-xl font-bold bg-gradient-to-r from-primary-500 via-orange-500 to-yellow-500 
                               bg-clip-text text-transparent bg-[length:200%_auto]
                               animate-gradient transition-all duration-300
                               group-hover/link:tracking-wider">
                  @TechyCSR
                </span>
                
                {/* Underline animation */}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-500 via-orange-500 to-yellow-500 
                               transition-all duration-300 group-hover/link:w-full rounded-full" />
                
                {/* Glow effect on hover */}
                <span className="absolute inset-0 -z-10 blur-xl opacity-0 group-hover/link:opacity-50 
                               bg-gradient-to-r from-primary-500 via-orange-500 to-yellow-500 
                               transition-opacity duration-300 scale-150" />
              </a>
            </div>

            {/* Decorative corner accents */}
            <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-primary-500/30 rounded-tl-lg 
                           opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-primary-500/30 rounded-tr-lg 
                           opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-primary-500/30 rounded-bl-lg 
                           opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-primary-500/30 rounded-br-lg 
                           opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        </div>
      </div>

      {/* Bottom gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />
      
      {/* Add keyframes for gradient animation */}
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </footer>
  );
};

export default Footer;
